import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { LoadingSpinner, IconRefresh, IconSearch, IconChevronRight, IconChevronLeft, IconFilter, IconCheck, IconAlert } from '../components/ui/Icons';

const PlantEnrichmentDashboard = () => {
    const { user, fetchWithAuth, authToken } = useContext(AuthContext);
    const { showToast } = useToast();

    // Stats State
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // List State
    const [plants, setPlants] = useState([]);
    const [totalPlants, setTotalPlants] = useState(0);
    const [listLoading, setListLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [minComp, setMinComp] = useState(0);
    const [maxComp, setMaxComp] = useState(100);
    const [sortBy, setSortBy] = useState('data_completeness');

    // Selection State
    const [selectedIds, setSelectedIds] = useState([]);

    // Task State
    const [activeTask, setActiveTask] = useState(null);
    const [progress, setProgress] = useState(null);
    const [logs, setLogs] = useState([]);

    const fetchStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const res = await fetchWithAuth('/api/enrichment/stats/');
            if (res.ok) {
                const data = await res.json();
                setStats(data);
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        } finally {
            setStatsLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchPlants = useCallback(async () => {
        setListLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                page_size: pageSize,
                q: searchQuery,
                min_completeness: minComp,
                max_completeness: maxComp,
                sort: sortBy
            });
            const res = await fetchWithAuth(`/api/enrichment/plants/?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                setPlants(data.results);
                setTotalPlants(data.total);
            }
        } catch (error) {
            console.error("Error fetching plants:", error);
        } finally {
            setListLoading(false);
        }
    }, [fetchWithAuth, page, pageSize, searchQuery, minComp, maxComp, sortBy]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchPlants();
    }, [fetchPlants]);

    useEffect(() => {
        let interval;
        if (activeTask) {
            interval = setInterval(checkProgress, 2000);
        }
        return () => clearInterval(interval);
    }, [activeTask]);

    const checkProgress = async () => {
        if (!activeTask) return;
        try {
            const res = await fetchWithAuth(`/api/admin/scraping/progress/${activeTask}/`);
            if (res.ok) {
                const data = await res.json();
                if (data.success && data.progress) {
                    setProgress(data.progress);
                    if (data.progress.detailed_logs) {
                        setLogs(data.progress.detailed_logs);
                    }
                    if (data.progress.status === 'completed' || data.progress.status === 'failed' || data.progress.status === 'cancelled') {
                        setActiveTask(null);
                        showToast(`Aufgabe ${data.progress.status === 'completed' ? 'abgeschlossen' : 'abgebrochen'}`, 'info');
                        fetchStats();
                        fetchPlants();
                    }
                }
            }
        } catch (error) {
            console.error("Progress fetch error", error);
        }
    };

    const handleBatchEnrich = async (force = false) => {
        if (selectedIds.length === 0) {
            showToast('Keine Pflanzen ausgewählt', 'error');
            return;
        }

        try {
            const res = await fetchWithAuth('/api/enrichment/batch/', {
                method: 'POST',
                body: JSON.stringify({
                    action: 'enrich',
                    plant_ids: selectedIds,
                    sources: ['all'],
                    force: force
                })
            });
            const data = await res.json();
            if (data.success && data.task_id) {
                setActiveTask(data.task_id);
                showToast('Batch-Enrichment gestartet', 'success');
                setSelectedIds([]);
            } else {
                showToast(data.error || 'Fehler beim Starten', 'error');
            }
        } catch (error) {
            showToast('Verbindungsfehler', 'error');
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === plants.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(plants.map(p => p.id));
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    if (!user || !user.is_staff) {
        return <div className="p-8 text-center">Zugriff verweigert.</div>;
    }

    return (
        <div className="min-h-screen bg-[#f8fafc] dark:bg-[#0f172a] p-4 md:p-8 transition-colors duration-500">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* Header */}
                <div className="relative overflow-hidden bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-xl border border-slate-200 dark:border-slate-700">
                    <div className="absolute top-0 right-0 -mt-12 -mr-12 w-64 h-64 bg-plantlix-500/10 rounded-full blur-3xl"></div>
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <h1 className="text-4xl font-black text-slate-800 dark:text-white tracking-tight mb-2">
                                Data <span className="text-plantlix-500">Enrichment</span> Hub
                            </h1>
                            <p className="text-slate-500 dark:text-slate-400 font-medium">
                                Analysiere und maximiere die Datenqualität deiner Pflanzensammlung.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={fetchStats}
                                className="p-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-2xl transition-all"
                            >
                                <IconRefresh className={`w-5 h-5 ${statsLoading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quick Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[
                        { label: 'Gesamt Pflanzen', value: stats?.total_plants || 0, icon: '🌿', color: 'blue' },
                        { label: 'Ø Vollständigkeit', value: `${stats?.avg_completeness || 0}%`, icon: '📊', color: 'green' },
                        { label: 'Unvollständig (<50%)', value: stats?.ranges?.['0-20'] + stats?.ranges?.['20-40'] || 0, icon: '⚠️', color: 'amber' },
                        { label: 'Exzellent (>80%)', value: stats?.ranges?.['80-100'] || 0, icon: '💎', color: 'emerald' },
                    ].map((stat, i) => (
                        <div key={i} className="bg-white dark:bg-slate-800 rounded-3xl p-6 shadow-lg border border-slate-100 dark:border-slate-700 hover:scale-[1.02] transition-transform duration-300">
                            <div className="flex items-center gap-4">
                                <span className="text-4xl">{stat.icon}</span>
                                <div>
                                    <p className="text-sm font-bold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                    <p className="text-2xl font-black text-slate-800 dark:text-white">{stat.value}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Progress Tracking (Conditional) */}
                {activeTask && (
                    <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-2xl border-2 border-plantlix-500/20 animate-in fade-in slide-in-from-top duration-500">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800 dark:text-white">🚀 Enrichment läuft...</h3>
                                <p className="text-slate-500 dark:text-slate-400 text-sm">Task ID: {activeTask}</p>
                            </div>
                            <div className="text-right">
                                <span className="text-3xl font-black text-plantlix-500">{progress?.percentage || 0}%</span>
                            </div>
                        </div>

                        <div className="w-full bg-slate-200 dark:bg-slate-700 h-4 rounded-full overflow-hidden mb-6">
                            <div
                                className="h-full bg-gradient-to-r from-plantlix-400 to-plantlix-600 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${progress?.percentage || 0}%` }}
                            ></div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-4 border border-slate-200 dark:border-slate-700">
                                <h4 className="font-bold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 bg-plantlix-500 rounded-full animate-pulse"></span>
                                    Aktueller Status
                                </h4>
                                <div className="space-y-2 text-sm">
                                    <p className="flex justify-between">
                                        <span className="text-slate-400">Pflanze:</span>
                                        <span className="font-bold text-slate-700 dark:text-slate-300">{progress?.current_plant || 'Initialisiere...'}</span>
                                    </p>
                                    <p className="flex justify-between">
                                        <span className="text-slate-400">Operation:</span>
                                        <span className="text-slate-700 dark:text-slate-300">{progress?.current_operation || '-'}</span>
                                    </p>
                                    <div className="pt-2 flex justify-between gap-4">
                                        <div className="flex-1 bg-plantlix-100 dark:bg-plantlix-900/30 text-plantlix-700 dark:text-plantlix-400 p-2 rounded-xl text-center">
                                            <p className="text-xs font-bold uppercase">Erfolgreich</p>
                                            <p className="text-lg font-black">{progress?.stats?.updated || 0}</p>
                                        </div>
                                        <div className="flex-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 p-2 rounded-xl text-center">
                                            <p className="text-xs font-bold uppercase">Fehler</p>
                                            <p className="text-lg font-black">{progress?.stats?.errors || 0}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-slate-900 rounded-2xl p-4 font-mono text-xs overflow-hidden">
                                <h4 className="text-slate-500 mb-2 uppercase tracking-widest text-[10px] font-bold">System Logs</h4>
                                <div className="h-24 overflow-y-auto space-y-1 custom-scrollbar">
                                    {logs.slice().reverse().map((log, i) => (
                                        <div key={i} className={`flex gap-2 ${log.level === 'error' ? 'text-red-400' : log.level === 'success' ? 'text-plantlix-400' : 'text-slate-400'}`}>
                                            <span className="opacity-30">[{new Date(log.timestamp).toLocaleTimeString()}]</span>
                                            <span>{log.message}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content Card */}
                <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {/* Filters Bar */}
                    <div className="p-6 border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="relative flex-1 w-full">
                            <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Pflanze suchen..."
                                className="w-full bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 rounded-2xl py-3 pl-12 pr-4 focus:border-plantlix-500 focus:outline-none transition-colors dark:text-white"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center gap-3 w-full md:w-auto">
                            <div className="flex items-center bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-200 dark:border-slate-700 p-1">
                                <button
                                    onClick={() => handleBatchEnrich(false)}
                                    disabled={selectedIds.length === 0 || activeTask}
                                    className="px-6 py-2 bg-plantlix-500 hover:bg-plantlix-600 disabled:bg-slate-200 dark:disabled:bg-slate-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-plantlix-500/20"
                                >
                                    ✨ Enrich Selected ({selectedIds.length})
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-900/50 text-slate-400 text-xs font-black uppercase tracking-widest">
                                    <th className="p-6 w-10">
                                        <input type="checkbox" checked={selectedIds.length === plants.length && plants.length > 0} onChange={toggleSelectAll} className="w-5 h-5 rounded border-2 border-slate-300 text-plantlix-500 focus:ring-plantlix-500" />
                                    </th>
                                    <th className="p-6">Pflanze</th>
                                    <th className="p-6 text-center">Vollständigkeit</th>
                                    <th className="p-6 text-right">Zuletzt aktualisiert</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {listLoading ? (
                                    <tr>
                                        <td colSpan="4" className="p-12 text-center">
                                            <LoadingSpinner />
                                        </td>
                                    </tr>
                                ) : plants.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-12 text-center text-slate-500">
                                            Keine Pflanzen gefunden.
                                        </td>
                                    </tr>
                                ) : plants.map((plant) => (
                                    <tr key={plant.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors group">
                                        <td className="p-6">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(plant.id)}
                                                onChange={() => toggleSelect(plant.id)}
                                                className="w-5 h-5 rounded border-2 border-slate-300 text-plantlix-500 focus:ring-plantlix-500"
                                            />
                                        </td>
                                        <td className="p-6">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-slate-800 dark:text-white italic">{plant.botanical_name}</span>
                                                <span className="text-sm text-slate-500 font-medium">{plant.german_name || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="p-6">
                                            <div className="flex flex-col items-center gap-2">
                                                <div className="w-32 bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${plant.data_completeness > 80 ? 'bg-plantlix-500' :
                                                            plant.data_completeness > 50 ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`}
                                                        style={{ width: `${plant.data_completeness}%` }}
                                                    ></div>
                                                </div>
                                                <span className="text-xs font-black text-slate-600 dark:text-slate-400">{plant.data_completeness}%</span>
                                            </div>
                                        </td>
                                        <td className="p-6 text-right text-sm text-slate-500 font-mono">
                                            {plant.last_enriched ? new Date(plant.last_enriched).toLocaleDateString() : 'Nie'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="p-6 bg-slate-50/50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
                        <span className="text-sm text-slate-500 font-medium">
                            Zeige {Math.min(pageSize, totalPlants)} von {totalPlants} Pflanzen
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 disabled:opacity-50 transition-all shadow-sm"
                            >
                                <IconChevronLeft className="w-5 h-5" />
                            </button>
                            <div className="flex items-center px-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-slate-700 dark:text-slate-300">
                                {page}
                            </div>
                            <button
                                onClick={() => setPage(p => p + 1)}
                                disabled={page * pageSize >= totalPlants}
                                className="p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 disabled:opacity-50 transition-all shadow-sm"
                            >
                                <IconChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Styles for Scrollbar */}
            <style>
                {`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #334155;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #475569;
                }
                `}
            </style>
        </div>
    );
};

export default PlantEnrichmentDashboard;
